if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        if (this.dataset.desktopLayout === 'thumbnail_slider' && !this.verticalThumbnailsInitialized) {
          this.verticalThumbnailsInitialized = true;
          this.initVerticalThumbnails();
        }
      }

      initVerticalThumbnails() {
        const thumbComponent = this.elements.thumbnails;
        if (!thumbComponent) return;

        const list = thumbComponent.querySelector('[id^="Slider-Thumbnails"]');
        const nextButton = thumbComponent.querySelector('.slider-button--next');
        const prevButton = thumbComponent.querySelector('.slider-button--prev');
        if (!list || !nextButton) return;

        const scrollMin = parseInt(this.dataset.thumbnailScrollMin || '6', 10);
        const mediaCount = parseInt(this.dataset.mediaCount || '0', 10);
        const needsScroll = mediaCount > scrollMin;

        const setButtonState = (button, isDisabled) => {
          if (!button) return;
          button.removeAttribute('disabled');
          if (isDisabled) button.setAttribute('aria-disabled', 'true');
          else button.removeAttribute('aria-disabled');
        };

        const getScrollStep = () => {
          const item = list.querySelector('.thumbnail-list__item');
          if (!item) return 100;
          const styles = window.getComputedStyle(list);
          const gap = parseFloat(styles.rowGap || styles.gap || 0) || 0;
          return item.offsetHeight + gap;
        };

        const updateVerticalButtons = () => {
          if (!this.mql.matches) return;

          if (!needsScroll) {
            thumbComponent.classList.remove('thumbnail-slider--scrollable');
            list.classList.remove('thumbnail-list--has-scroll');
            prevButton?.classList.add('hidden');
            nextButton.classList.add('hidden');
            return;
          }

          thumbComponent.classList.add('thumbnail-slider--scrollable');
          list.classList.add('thumbnail-list--has-scroll');

          const maxScroll = list.scrollHeight - list.clientHeight;
          const isScrollable = maxScroll > 1;

          if (prevButton) {
            if (!isScrollable) {
              setButtonState(prevButton, true);
              prevButton.classList.add('hidden');
            } else {
              prevButton.classList.remove('hidden');
              setButtonState(prevButton, list.scrollTop <= 1);
            }
          }

          if (!isScrollable) {
            setButtonState(nextButton, true);
            nextButton.classList.add('hidden');
          } else {
            nextButton.classList.remove('hidden');
            setButtonState(nextButton, list.scrollTop >= maxScroll - 1);
          }
        };

        const scrollThumbnails = (direction) => {
          if (!this.mql.matches || !needsScroll) return;
          list.scrollTo({
            top: list.scrollTop + direction * getScrollStep(),
            behavior: 'smooth',
          });
          window.setTimeout(updateVerticalButtons, 300);
        };

        const onVerticalButtonClick = (event) => {
          if (!this.mql.matches || !needsScroll) return;
          if (event.currentTarget.getAttribute('aria-disabled') === 'true') return;
          event.preventDefault();
          scrollThumbnails(event.currentTarget.name === 'next' ? 1 : -1);
        };

        nextButton.addEventListener('click', onVerticalButtonClick);
        prevButton?.addEventListener('click', onVerticalButtonClick);
        list.addEventListener('scroll', updateVerticalButtons);
        this.mql.addEventListener('change', updateVerticalButtons);
        window.addEventListener('resize', updateVerticalButtons);
        window.setTimeout(updateVerticalButtons, 100);
      }

      onSlideChanged(event) {
        const currentElement = event.detail.currentElement;
        if (!currentElement) return;

        this.updateActiveSlide(currentElement);

        if (!this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      updateActiveSlide(activeSlide) {
        this.elements.viewer.querySelectorAll('.product__media-item').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeSlide.classList.add('is-active');
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.updateActiveSlide(activeMedia);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.dataset.desktopLayout === 'thumbnail_slider' && this.mql.matches) {
          thumbnail.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
